<template>
  <table class="calendar">
    <thead class="calendar__header">
      <tr>
        <th
          class="calendar__arrow calendar__arrow_left"
          :class="[isPrevMonthAvailable ? 'calendar__arrow_enabled' : 'calendar__arrow_disabled']"
          @click="$emit('prevMonth')"
        >
          <MIcon
            v-if="arrowLeft"
            :icon="bulletArrowLeft"
            class="calendar__arrow-icon calendar__arrow-icon_left"
          />
        </th>
        <th
          colspan="5"
          class="calendar__month"
        >
          {{ monthName }} {{ year }}
        </th>
        <th
          class="calendar__arrow calendar__arrow_right"
          :class="[isNextMonthAvailable ? 'calendar__arrow_enabled' : 'calendar__arrow_disabled']"
          @click="$emit('nextMonth')"
        >
          <MIcon
            v-if="arrowRight"
            :icon="bulletArrowRight"
            class="calendar__arrow-icon calendar__arrow-icon_right"
          />
        </th>
      </tr>
    </thead>
    <tbody class="calendar__body">
      <tr>
        <th
          v-for="weekDay in locale.daysOfWeek"
          :key="weekDay"
          class="calendar__weekday"
        >
          {{ weekDay }}
        </th>
      </tr>
      <tr
        v-for="(dateRow, index) in calendar"
        :key="index"
      >
        <template v-for="(date, idx) in dateRow">
          <td
            :key="idx"
            class="calendar__day"
            :class="dayClass(date)"
            @click="$emit('dateClick', date)"
          >
            <div
              v-if="date.month() === month"
              class="calendar__day-left-half"
            />
            <div
              v-if="date.month() === month"
              class="calendar__day-number"
            >
              {{ date | dateNum }}
            </div>
            <div
              v-if="date.month() === month"
              class="calendar__day-right-half"
            />
          </td>
        </template>
      </tr>
    </tbody>
  </table>
</template>

<script>
  import {
    isDayEqual,
    isDayAfter,
    isDayAfterOrEqual,
    isDayBefore,
    isDayBeforeOrEqual
  } from 'components/Inputs/Dates/DateRangePicker/util';
  import {momentWrapper} from 'components/Inputs/Dates/utils/momentWrapper';
  import bulletArrowRight from 'assets/icons/bullet-arrow-right.svg';
  import bulletArrowLeft from 'assets/icons/bullet-arrow-left.svg';

  import MIcon from 'components/Design/MIcon.vue';

  export default {
    name: 'Calendar',
    filters: {
      dateNum (value) {
        return value.date();
      }
    },
    components: {MIcon},
    props: {
      monthDate: Date,
      locale: {
        type: Object,
        default: () => ({})
      },
      startDate: {
        type: Date,
        default: null
      },
      endDate: {
        type: Date,
        default: null
      },
      minDate: {
        type: Date,
        default: null
      },
      maxDate: {
        type: Date,
        default: null
      },
      arrowRight: {
        type: Boolean,
        default: true
      },
      arrowLeft: {
        type: Boolean,
        default: true
      },
      isPrevMonthAvailable: {
        type: Boolean,
        default: true
      },
      isNextMonthAvailable: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        bulletArrowRight,
        bulletArrowLeft
      };
    },
    computed: {
      monthName () {
        return momentWrapper().lang().
          months(momentWrapper(this.monthDate), 'nominative');
      },
      year: {
        get () {
          return this.monthDate.getFullYear();
        },
        set (value) {
          this.$emit('change-month', {
            month: this.month,
            year: value
          });
        }
      },
      month: {
        get () {
          return this.monthDate.getMonth();
        },
        set (value) {
          this.$emit('change-month', {
            month: value,
            year: this.year
          });
        }
      },
      calendar () {
        let month = this.month;
        let year = this.monthDate.getFullYear();
        let firstDay = new Date(year, month, 1);
        let lastMonth = momentWrapper(firstDay).subtract(1, 'month').
          month();
        let lastYear = momentWrapper(firstDay).subtract(1, 'month').
          year();
        let daysInLastMonth = momentWrapper([lastYear, lastMonth]).daysInMonth();

        const dayOfWeek = firstDay.getDay();

        const calendar = [[]];

        let startDay = daysInLastMonth - dayOfWeek + this.locale.firstDay + 1;
        if (startDay > daysInLastMonth) {
          startDay -= 7;
        }

        let curDate = momentWrapper([lastYear, lastMonth, startDay, 12, 0, 0]);
        if (dayOfWeek === this.locale.firstDay) {
          curDate = momentWrapper(firstDay);
        } else {
          startDay = daysInLastMonth - 6;
        }

        for (let i = 0, col = 0, row = 0; ; i += 1, col += 1, curDate = momentWrapper(curDate).add(1, 'day')) {
          if (i > 0 && col % 7 === 0) {
            col = 0;
            row += 1;
            if (curDate.month() !== month) {
              break;
            }
            calendar.push([]);
          }
          calendar[row][col] = curDate.clone();
          curDate.hour(12);
        }

        return calendar;
      }
    },
    methods: {
      dayClass (date) {
        return {
          calendar__day_off: date.month() !== this.month,
          calendar__day_weekend: date.isoWeekday() > 5,
          calendar__day_today: !this.startDate && !this.endDate && isDayEqual(date, new Date()),
          calendar__day_active: this.startDate && this.endDate &&
            (isDayEqual(date, this.startDate) || isDayEqual(date, this.endDate)),
          calendar__day_in_range: isDayAfterOrEqual(date, this.startDate) && isDayBeforeOrEqual(date, this.endDate),
          calendar__day_start_date: isDayEqual(date, this.startDate),
          calendar__day_end_date: isDayEqual(date, this.endDate),
          calendar__day_disabled: this.minDate && isDayBefore(date, this.minDate) ||
            this.maxDate && isDayAfter(date, this.maxDate),
          calendar__day_first_of_month: date.date() === 1,
          calendar__day_last_of_month: date.date() === date.daysInMonth()
        };
      }
    }
  };
</script>

<style>
  .calendar {
    --calendar-day-size: 22px;

    font-family: var(--main-font-family);
    font-size: var(--secondary-font-size);
    font-weight: var(--main-font-weight);

    color: var(--text-color);

    width: 192px;

    background-color: var(--background-color);

    border-spacing: 0 4px;
    border-collapse: separate;
  }

  /* reset application.css */
  .calendar__body th {
    font-weight: var(--main-font-weight);
  }

  /* reset application.css */
  .calendar__header th {
    font-weight: var(--medium-font-weight);
  }

  .calendar__header {
    font-family: var(--main-font-family);
    font-size: var(--main-font-size);
    font-weight: var(--medium-font-weight);

    text-transform: capitalize;
    user-select: none;
  }

  .calendar__arrow-icon {
    width: 5px;
    height: 8px;
  }

  .calendar__month {
    width: auto;
  }

  .calendar__arrow,
  .calendar__month,
  .calendar__weekday,
  .calendar__day {
    text-align: center;
    vertical-align: middle;

    white-space: nowrap;

    width: 28px;
    min-width: 0;
    height: var(--calendar-day-size);

    padding: 2px;
  }

  .calendar__weekday {
    color: var(--gray-text-color);
  }

  .calendar__day,
  .calendar__arrow_enabled {
    cursor: pointer;
  }

  .calendar__day {
    position: relative;
    padding: 2px 0 0;
  }

  .calendar__day_disabled,
  .calendar__arrow_disabled {
    color: var(--gray-text-color);

    cursor: not-allowed;

    pointer-events: none;
  }

  .calendar__day-number {
    /* for vertical aligning text */
    line-height: var(--calendar-line-height);

    position: absolute;
    top: 0;
    right: 2px;

    width: var(--calendar-day-size);
    height: var(--calendar-day-size);

    border-radius: 50%;
  }

  .calendar__day_today .calendar__day-number {
    background-color: var(--gray-color);
    border: 1px solid var(--gray-color);
  }

  .calendar__day_start_date .calendar__day-number,
  .calendar__day_end_date .calendar__day-number {
    color: var(--background-color);

    z-index: 2;

    background-color: var(--action-color);

    border: 1px solid var(--action-color);
  }

  .calendar__day-right-half,
  .calendar__day-left-half {
    position: absolute;
    top: 0;
    z-index: 1;

    width: 14px;
    height: var(--calendar-day-size);

    padding-top: 2px;

    background: transparent;
  }

  .calendar__day-right-half {
    right: 0;
  }

  .calendar__day-left-half {
    left: 0;
  }

  .calendar__day_in_range,
  .calendar__day_start_date .calendar__day-right-half,
  .calendar__day_end_date .calendar__day-left-half {
    background-color: var(--action-background-color);
  }

  .calendar__day_in_range.calendar__day_off,
  .calendar__day_start_date .calendar__day-left-half,
  .calendar__day_end_date .calendar__day-right-half,
  .calendar__day_start_date.calendar__day_end_date .calendar__day-right-half,
  .calendar__day_start_date.calendar__day_end_date .calendar__day-left-half {
    background: transparent;
    border: transparent;
  }

  .calendar__day_in_range.calendar__day_start_date,
  .calendar__day_in_range.calendar__day_end_date {
    background: transparent;
  }

  .calendar__day_in_range:last-child,
  .calendar__day_in_range.calendar__day_last_of_month,
  .calendar__day_in_range:last-child .calendar__day-right-half,
  .calendar__day_in_range.calendar__day_last_of_month .calendar__day-right-half {
    border-radius: 0 5px 5px 0;
  }

  .calendar__day_in_range:first-child,
  .calendar__day_in_range.calendar__day_first_of_month,
  .calendar__day_in_range:first-child .calendar__day-left-half,
  .calendar__day_in_range.calendar__day_first_of_month .calendar__day-left-half {
    border-radius: 5px 0 0 5px;
  }
</style>
